import React, { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Settings2, GripVertical } from 'lucide-react'

export interface ColumnConfig {
  id: string
  label: string
  visible: boolean
  order: number
}

interface DataTableColumnConfigProps {
  columns: ColumnConfig[]
  onColumnToggle: (columnId: string) => void
  onColumnReorder: (dragIndex: number, hoverIndex: number) => void
}

export function DataTableColumnConfig({
  columns,
  onColumnToggle,
  onColumnReorder
}: DataTableColumnConfigProps) {
  const [draggedItem, setDraggedItem] = useState<number | null>(null)

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedItem(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, hoverIndex: number) => {
    e.preventDefault()
    if (draggedItem !== null && draggedItem !== hoverIndex) {
      onColumnReorder(draggedItem, hoverIndex)
    }
    setDraggedItem(null)
  }

  const sortedColumns = columns.sort((a, b) => a.order - b.order)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings2 className="h-4 w-4 mr-2" />
          Customize Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-4">
          <div>
            <h4 className="font-medium">Column Configuration</h4>
            <p className="text-sm text-muted-foreground">Select and reorder columns</p>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {sortedColumns.map((column, index) => (
              <div 
                key={column.id} 
                className="flex items-center space-x-2 p-2 border rounded-md hover:bg-muted/50 cursor-move"
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <Checkbox
                  id={column.id}
                  checked={column.visible}
                  onCheckedChange={() => onColumnToggle(column.id)}
                />
                <Label
                  htmlFor={column.id}
                  className="text-sm font-normal cursor-pointer flex-1"
                >
                  {column.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}